import { map, share, shareReplay, tap } from 'rxjs/operators'
import { Observable } from 'rxjs'
import { inject, Injectable } from '@angular/core'
import { BreakpointObserver, Breakpoints as BP } from '@angular/cdk/layout'
import {
	Breakpoints,
	BreakpointSection,
	ScreenSize,
	ScreenSizes,
} from '@lg/shared'
import { ChangeDetectionService } from './change-detection.service.js'
import { toSignal } from '@angular/core/rxjs-interop'

@Injectable({
	providedIn: 'root',
})
export class LayoutService {
	private breakpointObserver = inject(BreakpointObserver)
	private cdService = inject(ChangeDetectionService)

	breakpoints$: Observable<Breakpoints> = this.breakpointObserver
		.observe([
			BP.XSmall,
			BP.Small,
			BP.Medium,
			BP.Large,
			BP.XLarge,
			BP.Handset,
			BP.HandsetPortrait,
			BP.HandsetLandscape,
			BP.Tablet,
			BP.TabletPortrait,
			BP.TabletLandscape,
			BP.Web,
			BP.WebPortrait,
			BP.WebLandscape,
		])
		.pipe(
			map((state) => {
				const queries = ScreenSizes.map((size) => BP[size])

				const matchedIdx = queries.findIndex(
					(query) => state.breakpoints[query],
				)

				const isBreakpointMatched = (bpName: string) => {
					const nameParts = bpName.split(',').map((part) => part.trim())
					return nameParts.some((part) => state.breakpoints[part])
				}

				const bps: Breakpoints = {
					sizeEquals: Object.fromEntries(
						ScreenSizes.map((size, idx) => [size, matchedIdx == idx]),
					) as unknown as BreakpointSection,
					sizeIsGreaterThan: Object.fromEntries(
						ScreenSizes.map((size, idx) => [size, matchedIdx > idx]),
					) as unknown as BreakpointSection,
					sizeIsGreaterThanOrEqual: Object.fromEntries(
						ScreenSizes.map((size, idx) => [size, matchedIdx >= idx]),
					) as unknown as BreakpointSection,
					sizeIsLessThan: Object.fromEntries(
						ScreenSizes.map((size, idx) => [size, matchedIdx < idx]),
					) as unknown as BreakpointSection,
					sizeIsLessThanOrEqual: Object.fromEntries(
						ScreenSizes.map((size, idx) => [size, matchedIdx <= idx]),
					) as unknown as BreakpointSection,
					isHandset: isBreakpointMatched(BP.Handset),
					isHandsetPortrait: isBreakpointMatched(BP.HandsetPortrait),
					isHandsetLandscape: isBreakpointMatched(BP.HandsetLandscape),
					isTablet: isBreakpointMatched(BP.Tablet),
					isTabletPortrait: isBreakpointMatched(BP.TabletPortrait),
					isTabletLandscape: isBreakpointMatched(BP.TabletLandscape),
					isWeb: isBreakpointMatched(BP.Web),
					isWebPortrait: isBreakpointMatched(BP.WebPortrait),
					isWebLandscape: isBreakpointMatched(BP.WebLandscape),
					isPortrait:
						isBreakpointMatched(BP.HandsetPortrait) ||
						isBreakpointMatched(BP.TabletPortrait) ||
						isBreakpointMatched(BP.WebPortrait),
					isLandscape:
						isBreakpointMatched(BP.HandsetLandscape) ||
						isBreakpointMatched(BP.TabletLandscape) ||
						isBreakpointMatched(BP.WebLandscape),
					screenSize: ScreenSizes[matchedIdx],
					decideBySize<T>(
						options: Partial<Record<ScreenSize, T>> | [T, T, T, T, T],
					) {
						if (options instanceof Array) {
							const idx = ScreenSizes.findIndex((ss) => ss == this.screenSize)
							return (options[idx] ?? options.at(-1))!
						}
						let lastEncounteredValue: T = options.XSmall!

						for (const screenSize of ScreenSizes) {
							if (this.sizeIsLessThan[screenSize]) {
								return lastEncounteredValue
							}
							const option = options[screenSize]
							if (option !== undefined) lastEncounteredValue = option
						}
						return lastEncounteredValue
					},
				}
				return bps
			}),
			tap((bps) => {
				// TODO: change to computed signal and use on template
				document.body.classList.remove(...ScreenSizes)
				document.body.classList.add(bps.screenSize)

				this.cdService.refreshWholeApp()
			}),
			shareReplay(1),
		)
	breakpoints = toSignal(this.breakpoints$, { initialValue: {} as Breakpoints })
}
